import { Controller } from "stimulus";

export default class extends Controller {
  static get targets() {
    return ["serverSelect", "accessSelect"];
  }

  connect() {
    this.userClearedAccessList = this.isClearedValue(this.normalizeValue(this.accessSelectTarget?.value));
    this.update();
    this.ensureDropdownReady();
  }

  update() {
    if (!this.hasServerSelectTarget || !this.hasAccessSelectTarget) return;

    const selectedServerId = this.serverSelectTarget.value;
    const shouldDisable = selectedServerId && this.restrictedServerIds.includes(selectedServerId);

    if (shouldDisable) {
      this.disableAccessSelect();
    } else {
      this.enableAccessSelect();
    }
  }

  handleAccessChange(event) {
    if (!this.hasAccessSelectTarget) return;

    if (this.suppressChange) {
      this.suppressChange = false;
      return;
    }

    const value = this.normalizeValue(event.target.value);
    this.userClearedAccessList = this.isClearedValue(value);
    this.accessSelectTarget.value = value;

    if (this.userClearedAccessList) {
      this.previousAccessListValue = undefined;
    } else {
      this.previousAccessListValue = value;
    }
  }

  ensureDropdownReady() {
    if (!this.hasAccessSelectTarget) return;

    if (this.dropdownElement) {
      this.syncDropdownWithAccessSelect();
    } else {
      window.requestAnimationFrame(() => this.ensureDropdownReady());
    }
  }

  get dropdownElement() {
    if (!this.hasAccessSelectTarget) return null;
    return this.accessSelectTarget.closest('.ui.dropdown');
  }

  dropdownInstance() {
    const dropdownElement = this.dropdownElement;
    const jQuery = window.$;
    if (!dropdownElement || !jQuery?.fn?.dropdown) return null;

    return jQuery(dropdownElement);
  }

  syncDropdownWithAccessSelect() {
    const instance = this.dropdownInstance();
    if (!instance) return;

    const normalizedValue = this.normalizeValue(this.accessSelectTarget.value);
    this.accessSelectTarget.value = normalizedValue;

    this.suppressChange = true;
    if (this.isClearedValue(normalizedValue)) {
      instance.dropdown('set selected', this.clearedValue());
      this.userClearedAccessList = true;
    } else {
      instance.dropdown('set selected', normalizedValue);
      this.userClearedAccessList = false;
    }
    window.requestAnimationFrame(() => { this.suppressChange = false; });
  }

  get restrictedServerIds() {
    if (!this._restrictedServerIds) {
      const value = this.data.get("restrictedServerIds") || "";
      this._restrictedServerIds = value.split(",").map((id) => id.trim()).filter(Boolean);
    }

    return this._restrictedServerIds;
  }

  disableAccessSelect() {
    const select = this.accessSelectTarget;
    const currentValue = this.normalizeValue(select.value);

    if (this.previousAccessListValue === undefined && !this.isClearedValue(currentValue)) {
      this.previousAccessListValue = currentValue;
    }

    this.setAccessSelectValue(currentValue);
    select.setAttribute("disabled", "disabled");

    const dropdownElement = this.dropdownElement;
    dropdownElement?.classList.add('disabled');

    const instance = this.dropdownInstance();
    instance?.dropdown('hide');
  }

  enableAccessSelect() {
    const select = this.accessSelectTarget;
    select.removeAttribute("disabled");

    const dropdownElement = this.dropdownElement;
    dropdownElement?.classList.remove('disabled');

    const valueToRestore = this.previousAccessListValue;
    this.previousAccessListValue = undefined;

    if (!this.userClearedAccessList && valueToRestore && !this.isClearedValue(valueToRestore)) {
      this.setAccessSelectValue(valueToRestore);
    } else {
      this.syncDropdownWithAccessSelect();
    }
  }

  setAccessSelectValue(value) {
    const normalizedValue = this.normalizeValue(value);
    const select = this.accessSelectTarget;
    select.value = normalizedValue;
    this.userClearedAccessList = this.isClearedValue(normalizedValue);

    const instance = this.dropdownInstance();
    if (!instance) return;

    this.suppressChange = true;
    instance.dropdown('set selected', normalizedValue);
    instance.dropdown('refresh');
    window.requestAnimationFrame(() => { this.suppressChange = false; });
  }

  clearedValue() {
    return 'none';
  }

  normalizeValue(value) {
    if (value === undefined || value === null || value === '') {
      return this.clearedValue();
    }
    return String(value);
  }

  isClearedValue(value) {
    return value === undefined || value === null || value === '' || value === this.clearedValue();
  }
}
